// src/ui/SiteChrome.jsx — the header and footer the public pages share.
//
// Home and About are the only two pages a stranger ever sees, so they get one
// header and one footer defined once. The product itself deliberately does NOT
// use these: inside the app the nav rail is the navigation, and a marketing bar
// above a chat list would be a second, competing one.

import React, { useEffect, useState } from 'react';
import { HOME, ABOUT, APP, navigate } from '../services/siteRoute';
import BrandLottie from './BrandLottie';
import './site.css';

/* 🟢 THE MARK IS THE SAME ANIMATED ONE THE APP USES. It was the favicon SVG in an
   <img>; it is now BrandLottie, which is the identical component the chat list, the
   login gate and the QR screen render — so the site and the product show the same
   mark, from the same two files, and a change to it lands in both at once.

   🟢 THE TWO PLACES PLAY IT DIFFERENTLY, ON PURPOSE. The header is on screen from the
   first paint, so playing on mount would mean the draw-on happens while the page is
   still settling and is never seen deliberately — it shows the FINISHED mark and
   redraws on click. The footer is below the fold, where playing on mount guarantees it
   finishes before anyone can see it — so it waits for the viewport, and plays once.

   (Both light/dark variants live INSIDE the two JSON files, so this file needs no theme
   CSS — the same reason the favicon SVG needed none.) */

export function SiteHeader({ route }) {
  /* 🟢 The bar only grows a border once the page has actually moved. Painting a
     line under a header that is still sitting on the hero makes the hero look
     like a separate, shorter page. */
  const [stuck, setStuck] = useState(false);
  useEffect(() => {
    const onScroll = () => setStuck(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const go = (r) => (e) => { e.preventDefault(); navigate(r); };

  return (
    <header className={`sc-head ${stuck ? 'stuck' : ''}`}>
      <div className="sc-wrap">
        {/* 🟢 THE MARK CARRIES THE NAME NOW. The wordmark beside it was saying
            the same thing twice, and the logo is the thing people recognise.
            The name survives where it still has to: the aria-label, so a screen
            reader still hears "Allo home" rather than "link, image". */}
        <a className="sc-brand" href="/" onClick={go(HOME)} aria-label="Allo home">
          {/* 🟢 No `label` here: the anchor above already carries the accessible name,
              and naming the mark too would make a screen reader say it twice. The
              click that replays it is the same click that follows the link — the
              header is shared chrome and does not remount on a route change, so the
              animation finishes across the navigation rather than being cut off. */}
          {/* 🟥 46, AND IT MAY NOT GO MUCH HIGHER. `.sc-head .sc-wrap` is a FIXED
              height — 62px, and 56px on a phone — so a mark taller than that stops
              being a bigger logo and becomes a taller header, which is a different
              change nobody asked for. 46 leaves 8px of air on desktop and 5px on a
              phone, and stays the larger of the two marks. */}
          <BrandLottie size={46} bare trigger="click" />
        </a>

        <nav className="sc-nav" aria-label="Site">
          <a href="/"
            className={route === HOME ? 'on' : ''}
            onClick={go(HOME)}>Features</a>
          <a href="/about"
            className={route === ABOUT ? 'on' : ''}
            onClick={go(ABOUT)}>About</a>
        </nav>

        <a className="sc-cta" href="/app" onClick={go(APP)}>Open Allo Web</a>
      </div>
    </header>
  );
}

export function SiteFooter() {
  const go = (r) => (e) => { e.preventDefault(); navigate(r); };
  return (
    <footer className="sc-foot">
      <div className="sc-wrap">
        {/* 🟢 THE LINE SITS BESIDE THE MARK, NOT UNDER IT. It used to be a sibling of
            this block with `width: 100%`, which in a wrapping flex row is an
            instruction to take a line of its own. Moving it inside makes mark + line
            one object, so the footer reads as one row: [mark  line] … [nav]. */}
        <div className="sc-foot-brand">
          {/* 🟢 36 — deliberately smaller than the header's 46. The footer has no fixed
              height, so this one is free to grow; it is held back because the header
              mark is the one people navigate by and it should read as the primary. */}
          <BrandLottie size={36} bare trigger="visible" label="Allo" />
          <p className="sc-foot-note">
            Built by one person, still being built.
          </p>
        </div>
        <nav className="sc-foot-nav" aria-label="Footer">
          <a href="/" onClick={go(HOME)}>Features</a>
          <a href="/about" onClick={go(ABOUT)}>About</a>
          <a href="/app" onClick={go(APP)}>Open Allo Web</a>
          {/* ⚖️ the legal pages are static files (public/legal) — plain links, a full page load */}
          <a href="/terms">Terms</a>
          <a href="/privacy">Privacy</a>
          <a href="/community-guidelines">Community Guidelines</a>
          <a href="/cookies">Cookies</a>
        </nav>
      </div>
    </footer>
  );
}
